(function () {
  'use strict';

  if (typeof window === 'undefined' || window.frontendErrorMonitor) {
    return;
  }

  const STORAGE_KEY = 'askfelix.frontendErrorQueue';
  const DEDUPE_KEY = 'askfelix.frontendErrorSeen';
  const ENDPOINT = '/api/metrics/frontend-errors';
  const MAX_QUEUE_SIZE = 40;
  const MAX_REPORTS_PER_SESSION = 25;
  const REPORT_DELAY_MS = 1500;
  const DEDUPE_WINDOW_MS = 5 * 60 * 1000;
  const BRAND_ROBOT_WEBP_PATH = '/img/askfelix-robot-icon-transp.webp';
  const BRAND_ROBOT_PNG_FALLBACK = '/static/img/askfelix-robot-icon-transp.png';
  const CLOUDFLARE_INSIGHTS_HOST = 'static.cloudflareinsights.com';
  const CLOUDFLARE_BEACON_PATH = '/beacon.min.js';

  const state = {
    flushTimer: null,
    flushing: false,
    reportsSent: 0,
    fetchWrapped: false,
    consoleWrapped: false
  };

  function safeParse(raw, fallback) {
    if (!raw) return fallback;
    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? fallback : parsed;
    } catch (_) {
      return fallback;
    }
  }

  function safeReadQueue() {
    try {
      const parsed = safeParse(localStorage.getItem(STORAGE_KEY), []);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function persistQueue(queue) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify((queue || []).slice(-MAX_QUEUE_SIZE)));
    } catch (_) {}
  }

  function safeReadDedupe() {
    try {
      const parsed = safeParse(localStorage.getItem(DEDUPE_KEY), {});
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  function persistDedupe(map) {
    try {
      localStorage.setItem(DEDUPE_KEY, JSON.stringify(map || {}));
    } catch (_) {}
  }

  function truncate(value, limit) {
    if (value == null) return null;
    let text;
    try {
      text = String(value);
    } catch (_) {
      return null;
    }
    return text ? text.slice(0, limit) : null;
  }

  function redactSecret(rawUrl) {
    const text = truncate(rawUrl, 4096);
    if (!text) return text;
    try {
      const parsed = new URL(text, window.location.origin);
      if (!parsed.searchParams.has('secret')) {
        return text;
      }
      parsed.searchParams.set('secret', '[redacted]');
      return parsed.toString();
    } catch (_) {
      return text.replace(/([?&]secret=)[^&#]*/i, '$1[redacted]');
    }
  }

  function readActiveQuery() {
    try {
      const input = document.getElementById('query');
      if (input && typeof input.value === 'string' && input.value.trim()) {
        return input.value.trim().slice(0, 2048);
      }
      const url = new URL(window.location.href);
      return (
        (url.searchParams.get('pending_query') || url.searchParams.get('query') || '').trim().slice(0, 2048)
      );
    } catch (_) {
      return null;
    }
  }

  function simpleHash(value) {
    const input = String(value || '');
    let hash = 0;
    for (let i = 0; i < input.length; i += 1) {
      hash = ((hash << 5) - hash + input.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(16);
  }

  function buildFingerprint(payload) {
    return simpleHash([
      payload.error_type || '',
      payload.message || '',
      payload.source || '',
      payload.url || '',
      payload.status || ''
    ].join('||'));
  }

  function normalizeReason(reason) {
    if (!reason) return {};
    if (reason instanceof Error) {
      return {
        message: truncate(reason.message, 4000),
        stack: truncate(reason.stack, 12000),
        name: truncate(reason.name, 128)
      };
    }
    if (typeof reason === 'object') {
      try {
        return {
          message: truncate(JSON.stringify(reason), 4000)
        };
      } catch (_) {
        return {
          message: truncate(reason.toString ? reason.toString() : 'Unhandled rejection', 4000)
        };
      }
    }
    return {
      message: truncate(reason, 4000)
    };
  }

  function buildBasePayload(payload) {
    const normalized = payload && typeof payload === 'object' ? Object.assign({}, payload) : {};
    normalized.timestamp = Date.now();
    normalized.url = redactSecret(window.location.href);
    normalized.userAgent = truncate(navigator.userAgent, 512);
    normalized.query = truncate(readActiveQuery(), 2048);
    normalized.secret_present = Boolean(
      (window.userData && window.userData.secret) || window.location.search.indexOf('secret=') > -1
    );
    normalized.screenshot_requested = true;
    normalized.fingerprint = normalized.fingerprint || buildFingerprint(normalized);
    return normalized;
  }

  function shouldReport(payload) {
    if (!payload || !payload.fingerprint) return false;
    if (state.reportsSent >= MAX_REPORTS_PER_SESSION) return false;

    const dedupe = safeReadDedupe();
    const now = Date.now();
    const lastSeen = Number(dedupe[payload.fingerprint] || 0);
    if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) {
      return false;
    }

    dedupe[payload.fingerprint] = now;
    Object.keys(dedupe).forEach(key => {
      if (!dedupe[key] || now - Number(dedupe[key]) > DEDUPE_WINDOW_MS * 12) {
        delete dedupe[key];
      }
    });
    persistDedupe(dedupe);
    state.reportsSent += 1;
    return true;
  }

  function enqueue(payload) {
    const queue = safeReadQueue();
    queue.push(payload);
    persistQueue(queue);
  }

  async function transmit(payload) {
    const body = JSON.stringify(payload);

    try {
      if (navigator.sendBeacon) {
        const success = navigator.sendBeacon(
          ENDPOINT,
          new Blob([body], { type: 'application/json' })
        );
        if (success) return true;
      }
    } catch (_) {}

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'same-origin',
        keepalive: true
      });
      return Boolean(response && response.ok);
    } catch (_) {
      return false;
    }
  }

  async function flushQueue() {
    if (state.flushing) return;
    state.flushing = true;
    try {
      const queue = safeReadQueue();
      if (!queue.length) return;

      const remaining = [];
      for (const entry of queue) {
        if (!entry || typeof entry !== 'object') continue;
        const delivered = await transmit(entry);
        if (!delivered) {
          remaining.push(entry);
        }
      }
      persistQueue(remaining);
    } finally {
      state.flushing = false;
    }
  }

  function scheduleFlush() {
    if (state.flushTimer) return;
    state.flushTimer = window.setTimeout(() => {
      state.flushTimer = null;
      flushQueue().catch(() => {});
    }, REPORT_DELAY_MS);
  }

  function report(payload) {
    const normalized = buildBasePayload(payload);
    if (!shouldReport(normalized)) {
      return normalized.fingerprint;
    }
    enqueue(normalized);
    scheduleFlush();
    return normalized.fingerprint;
  }

  function normalizeConsoleArgs(args) {
    return args
      .map(arg => {
        if (arg instanceof Error) {
          return arg.message || arg.name || 'Error';
        }
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch (_) {
            return '[object]';
          }
        }
        return String(arg);
      })
      .join(' ')
      .slice(0, 4000);
  }

  function wrapConsoleError() {
    if (state.consoleWrapped || !window.console || typeof window.console.error !== 'function') {
      return;
    }
    state.consoleWrapped = true;
    const original = window.console.error;
    window.console.error = function (...args) {
      try {
        const message = normalizeConsoleArgs(args);
        if (message && message.indexOf('frontend-error-monitor') === -1) {
          const errorArg = args.find(arg => arg instanceof Error);
          report({
            error_type: 'console.error',
            category: 'console',
            message,
            stack: errorArg ? truncate(errorArg.stack, 12000) : null
          });
        }
      } catch (_) {}
      return original.apply(this, args);
    };
  }

  function shouldTrackFetch(url, status) {
    const text = truncate(url, 2048);
    if (!text) return false;
    if (text.indexOf(ENDPOINT) !== -1) return false;
    const isInteresting = text.indexOf('/query') !== -1 || text.indexOf('/api/') !== -1;
    if (!isInteresting) return false;
    if (status == null) return true;
    return Number(status) >= 400;
  }

  function wrapFetch() {
    if (state.fetchWrapped || typeof window.fetch !== 'function') {
      return;
    }
    state.fetchWrapped = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const requestUrl =
        typeof input === 'string' ? input : (input && input.url) || truncate(input, 2048) || '';
      const method = (init && init.method) || (input && input.method) || 'GET';

      return originalFetch(input, init)
        .then(response => {
          if (shouldTrackFetch(requestUrl, response && response.status)) {
            report({
              error_type: 'fetch_response_error',
              category: 'network',
              message: `HTTP ${response.status} for ${method} ${requestUrl}`,
              endpoint: truncate(requestUrl, 2048),
              method: truncate(method, 16),
              status: response.status
            });
          }
          return response;
        })
        .catch(error => {
          if (shouldTrackFetch(requestUrl)) {
            report({
              error_type: 'fetch_request_failed',
              category: 'network',
              message: truncate(error && error.message ? error.message : 'Request failed', 4000),
              stack: truncate(error && error.stack, 12000),
              endpoint: truncate(requestUrl, 2048),
              method: truncate(method, 16)
            });
          }
          throw error;
        });
    };
  }

  function recoverBrandRobotResource(target, rawUrl) {
    let pathname;
    try {
      pathname = new URL(rawUrl, window.location.origin).pathname;
    } catch (_) {
      return false;
    }

    if (!pathname.endsWith(BRAND_ROBOT_WEBP_PATH)) {
      return false;
    }

    const tagName = String(target && target.tagName || '').toUpperCase();
    if (tagName === 'LINK') {
      // A failed preload is only an optimization failure. The real <img>
      // request below will either load or take the fallback path.
      return String(target.rel || '').toLowerCase().split(/\s+/).includes('preload');
    }

    if (tagName !== 'IMG') {
      return false;
    }

    try {
      if (target.getAttribute('data-helix-brand-fallback-attempted') === 'true') {
        return false;
      }
      target.setAttribute('data-helix-brand-fallback-attempted', 'true');
      target.src = BRAND_ROBOT_PNG_FALLBACK;
      return true;
    } catch (_) {
      return false;
    }
  }

  // Plotly redraws a hidden or zero-size chart from its own resize handler and
  // rejects; the chart recovers on the next visible layout, so these are noise.
  const BENIGN_PLOTLY_REDRAW_MESSAGES = [
    /Something went wrong with axis scaling/,
    /Resize must be passed a displayed plot div element/
  ];

  function isBenignPlotlyRedrawError(message) {
    const text = String(message || '');
    return BENIGN_PLOTLY_REDRAW_MESSAGES.some(pattern => pattern.test(text));
  }

  function isOptionalThirdPartyResource(rawUrl) {
    try {
      const parsed = new URL(rawUrl, window.location.origin);
      const pathname = parsed.pathname || '';
      return (
        parsed.hostname.toLowerCase() === CLOUDFLARE_INSIGHTS_HOST &&
        (pathname === CLOUDFLARE_BEACON_PATH || pathname.startsWith(`${CLOUDFLARE_BEACON_PATH}/`))
      );
    } catch (_) {
      return false;
    }
  }

  function handleWindowError(event) {
    try {
      const target = event && event.target;
      if (target && target !== window && (target.src || target.href)) {
        const resourceUrl = target.src || target.href || target.currentSrc;
        // Cloudflare injects this optional analytics script outside the app's
        // bundle. Privacy tools and network policy commonly block it, which
        // does not affect application behavior and is not actionable here.
        if (isOptionalThirdPartyResource(resourceUrl)) {
          return;
        }
        if (recoverBrandRobotResource(target, resourceUrl)) {
          return;
        }
        report({
          error_type: 'resource_load_error',
          category: 'resource',
          message: truncate(`Failed to load resource: ${resourceUrl}`, 4000),
          source: redactSecret(resourceUrl)
        });
        return;
      }

      const errorMessage =
        (event && event.message) || (event && event.error && event.error.message) || '';
      if (isBenignPlotlyRedrawError(errorMessage)) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        return;
      }

      const error = event && event.error;
      report({
        error_type: 'window_error',
        category: 'runtime',
        message: truncate((event && event.message) || (error && error.message) || 'Window error', 4000),
        stack: truncate(error && error.stack, 12000),
        source: redactSecret((event && event.filename) || ''),
        line: event && typeof event.lineno === 'number' ? event.lineno : undefined,
        column: event && typeof event.colno === 'number' ? event.colno : undefined,
        isUnhandled: true
      });
    } catch (_) {}
  }

  function handleUnhandledRejection(event) {
    try {
      const reason = normalizeReason(event && event.reason);
      if (isBenignPlotlyRedrawError(reason.message)) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        return;
      }
      report({
        error_type: 'unhandled_rejection',
        category: 'promise',
        message: reason.message || 'Unhandled promise rejection',
        stack: reason.stack || null,
        context: reason.name || null,
        isUnhandled: true
      });
    } catch (_) {}
  }

  function init() {
    wrapConsoleError();
    wrapFetch();

    window.addEventListener('error', handleWindowError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('online', () => {
      flushQueue().catch(() => {});
    });
    window.addEventListener('pagehide', () => {
      flushQueue().catch(() => {});
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushQueue().catch(() => {});
      }
    });

    scheduleFlush();
  }

  window.frontendErrorMonitor = {
    flush: flushQueue,
    report,
    queueLength() {
      return safeReadQueue().length;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
